import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Phone, FileText, Clock, Shield, Zap, CheckCircle } from "lucide-react";

const Home = () => {
  const benefits = [
    {
      icon: Phone,
      title: "Zéro interruption",
      description: "Continuez vos consultations sans être dérangé par les appels."
    },
    {
      icon: FileText,
      title: "Messages clairs et écrits",
      description: "Consultez les transcriptions précises de chaque message vocal."
    },
    {
      icon: Clock,
      title: "Gain de temps quotidien",
      description: "Traitez vos messages quand vous le souhaitez, à votre rythme."
    }
  ];
  
  const features = [
    { icon: Shield, text: "Conforme RGPD" },
    { icon: Zap, text: "Transcription instantanée" },
    { icon: CheckCircle, text: "Interface simple" },
  ];
  
  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="section-spacing relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/10" />
          <div className="container-custom relative">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
              {/* Hero Content */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                    Vos messages médicaux,{" "}
                    <span className="text-gradient-primary">transcrits automatiquement</span>
                  </h1>
                  <p className="text-lg text-muted-foreground sm:text-xl">
                    La solution simple et sécurisée pour recevoir et consulter vos messages téléphoniques sans interruption.
                  </p>
                </div>
                
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Link to="/login">
                    <Button size="lg" className="w-full sm:w-auto">
                      Accéder à mon espace
                    </Button>
                  </Link>
                  <Link to="/how-it-works">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Découvrir le fonctionnement
                    </Button>
                  </Link>
                </div>
                
                {/* Features badges */}
                <div className="flex flex-wrap gap-4">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon className="h-4 w-4 text-primary" />
                        <span>{feature.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Hero Image */}
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Njg3Mzg1MjZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional médical avec technologie"
                  className="relative rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Benefits Section */}
        <section className="section-spacing bg-muted/30">
          <div className="container-custom">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold sm:text-4xl">Trois bénéfices clés</h2>
              <p className="text-lg text-muted-foreground">
                Fruitycall simplifie votre quotidien de professionnel de santé
              </p>
            </div>
            
            <div className="grid gap-8 md:grid-cols-3">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card key={index} className="card-interactive border-2">
                    <CardContent className="flex flex-col items-center p-8 text-center">
                      <div className="mb-4 rounded-full bg-primary/10 p-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="mb-2 text-xl font-semibold">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="section-spacing">
          <div className="container-custom">
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="flex flex-col items-center p-12 text-center">
                <h2 className="mb-4 text-3xl font-bold sm:text-4xl">
                  Prêt à simplifier votre quotidien ?
                </h2>
                <p className="mb-8 max-w-2xl text-lg text-muted-foreground">
                  Accédez dès maintenant à votre espace professionnel et commencez à recevoir vos messages transcrits.
                </p>
                <Link to="/login">
                  <Button size="lg">Accéder à mon espace</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      
      <PublicFooter />
    </div>
  );
};

export default Home;